import { generateUuid } from '@pkg/shared/utils/id.utils'
import type { ZodTemplateLiteral } from 'zod'
import { z } from 'zod'

export type EntityId<S extends string> = `${S}_${string}`

// biome-ignore lint/suspicious/noExplicitAny: Intentionally hide
export type AnyEntityId = EntityId<any>

export type EntityIdOf<TEntity> = TEntity extends { id: infer I extends AnyEntityId } ? I : AnyEntityId

export const anyEntityId = z.templateLiteral([z.string(), '_', z.string()])

export type ZodIdString<S extends string> = ZodTemplateLiteral<`${S}_${string}`>

export type IdRef<V, S extends string> = V & {
  id: EntityId<S>
}

export function isEntityIdType<V extends string>(val: unknown, type: V): val is EntityId<V> {
  return typeof val === 'string' && val.startsWith(type)
}

export const entityIdGeneratorFactory =
  <S extends string>(table: S) =>
  () =>
    `${table}_${generateUuid()}`

export const entityId = <S extends string>(table: S) =>
  ({
    generate: entityIdGeneratorFactory(table),
    schema: z.templateLiteral([table, '_', z.string()]).default(() => `${table}_${generateUuid()}` as never) as never,
    ref: z.templateLiteral([table, '_', z.string()]),
    Type: undefined as unknown as EntityId<S>,
    is: (val: unknown): val is EntityId<S> => typeof val === 'string' && val.startsWith(`${table}_`),
    prefix: `${table}_`,
  }) as EntityIdFactory<S>

export interface EntityIdFactory<S extends string> {
  generate: () => EntityId<S>
  schema: ZodIdString<S>
  ref: ZodIdString<S>
  Type: EntityId<S>
  is: (val: unknown) => val is EntityId<S>
  prefix: string
}
